import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'
import { graphql } from 'gatsby'
import {
  Container,
  Row,
  RowColumn,
  Section,
  SectionTitle,
} from 'chaoskit/src/components'
import { MDXRenderer } from 'gatsby-plugin-mdx'
import { isEmpty } from 'lodash'
import { Fragment, useRef } from 'react'
import { motion } from 'framer-motion'

import Data from '~components/Data'
import FreeSignUp from '~components/FreeSignUp'
import BaseLayout from '~layouts/BaseLayout'
import Hero, {
  HeroContent,
  HeroFormWrapper,
  HeroTitle,
  HeroTitleSub,
} from '~partials/Hero'
import StaticFooter from '~partials/footer/StaticFooter'
import FooterCTA from '~partials/footer/FooterCTA'
import ProductOverview from '~partials/product/ProductOverview'
import HelmetSEO from '~partials/global/HelmetSEO'
import NodeTypeTracker from '~partials/global/NodeTypeTracker'
import CustomerLogos from '~partials/global/CustomerLogos'
import MarketoForm from '~components/MarketoForm'
import track from '~helpers/track'
import { ReactComponent as KVPReliabilitySvg } from '~media/product/overview/kvp-reliability.svg'
import { ReactComponent as KVPTrackSvg } from '~media/product/overview/kvp-track.svg'
import { ReactComponent as KVPTrustSvg } from '~media/product/overview/kvp-trust.svg'
import { ReactComponent as KVPAvoidSvg } from '~media/product/overview/kvp-avoid.svg'
import { ReactComponent as KVPReduceSvg } from '~media/product/overview/kvp-reduce.svg'
import useReveal from '~hooks/useReveal'

const StylesProductVariables = {
  imageSize: 75,
  preventImageSize: 75,
}

const TitleSection = ({
  heading,
  content,
  headingClass,
  textAlign,
  contrastClass,
  formPosition,
}) => {
  const theme = useTheme()

  return (
    <div
      css={{
        textAlign: `${textAlign}`,

        [theme.mq.medium]: {
          textAlign: 'left',
        },
      }}
      className={contrastClass}
    >
      <HeroTitle as={headingClass}>
        <Data>{heading}</Data>
      </HeroTitle>
      <HeroTitleSub
        css={{
          fontSize:
            formPosition === 'left'
              ? theme.fontSize.base
              : theme.fontSize.large,
          lineHeight: theme.lineHeight.base,
          a: { textDecoration: 'underline' },
        }}
      >
        <MDXRenderer>{content}</MDXRenderer>
      </HeroTitleSub>
    </div>
  )
}

TitleSection.propTypes = {
  heading: PropTypes.string.isRequired,
  content: PropTypes.string.isRequired,
  headingClass: PropTypes.string.isRequired,
  textAlign: PropTypes.string.isRequired,
  contrastClass: PropTypes.string.isRequired,
  formPosition: PropTypes.string,
}

const FormSection = ({
  form,
  enableWorkEmailValidation,
  freeSignupUseGenericEmail,
  showFormSubContent,
}) => {
  const theme = useTheme()

  return (
    <HeroFormWrapper>
      {isEmpty(form) ? (
        <FreeSignUp useGenericEmail={freeSignupUseGenericEmail} />
      ) : (
        <Fragment>
          <SectionTitle
            align={{ medium: 'left' }}
            title={form.heading}
            css={{
              marginBottom: `${theme.space.large}px !important`,
            }}
          />
          {showFormSubContent && (
            <div
              css={{
                fontSize: theme.fontSize.large,
                marginBottom: theme.space.medium,
              }}
            >
              <Data>{form.content}</Data>
            </div>
          )}
          <MarketoForm
            id={form.embed}
            fieldValues={{ programID: form.programId }}
            thankYouMessage={form.thankYouMessage}
            displayOptions={{
              submitButtonCentered: false,
            }}
            onSuccess={() => {
              track('Form submitted', {
                label: `marketo-${form.embed}`,
              })
            }}
            enableWorkEmailValidation={enableWorkEmailValidation}
            css={{
              '.mktoRequiredField': {
                position: 'relative',
              },
            }}
          />
        </Fragment>
      )}
    </HeroFormWrapper>
  )
}

FormSection.propTypes = {
  form: PropTypes.object,
  enableWorkEmailValidation: PropTypes.bool,
  freeSignupUseGenericEmail: PropTypes.bool,
  showFormSubContent: PropTypes.bool,
}

const KeyValueProp = ({ data }) => {
  const theme = useTheme()

  const ref = useRef()

  const { variants, controls } = useReveal({ ref, direction: 'down' })

  let KVPIcon

  switch (data.feature) {
    case 'reliability':
      KVPIcon = KVPReliabilitySvg
      break
    case 'track':
      KVPIcon = KVPTrackSvg
      break
    case 'trust':
      KVPIcon = KVPTrustSvg
      break
    case 'avoid':
      KVPIcon = KVPAvoidSvg
      break
    case 'reduce':
      KVPIcon = KVPReduceSvg
      break
    default:
      KVPIcon = () => null
  }

  return (
    <RowColumn size={{ medium: 6 }}>
      <motion.div
        css={{
          display: 'grid',
          gap: theme.space.base,
          alignItems: 'center',

          gridTemplateColumns: `${
            StylesProductVariables.preventImageSize / 1.5
          }px 1fr`,

          [theme.mq.medium]: {
            gridTemplateColumns: `${StylesProductVariables.preventImageSize}px 1fr`,
            alignItems: 'start',
          },
        }}
        ref={ref}
        variants={variants}
        animate={controls}
        initial="hidden"
      >
        <div>
          <KVPIcon
            css={{
              height: `${StylesProductVariables.imageSize}px`,
              width: `${StylesProductVariables.imageSize}px`,
              display: 'block',
            }}
          />
        </div>
        <div>
          <h5>{data.heading}</h5>
          <div css={{ ...theme.text.small }}>
            <Data>{data.blurb}</Data>
          </div>
        </div>
      </motion.div>
    </RowColumn>
  )
}

KeyValueProp.propTypes = {
  data: PropTypes.object.isRequired,
}

const SignUpLandingPage = ({
  data: {
    mdx: {
      frontmatter: {
        seoTitle,
        description,
        socialMediaImage,
        hero,
        freeSignupUseGenericEmail,
        enableWorkEmailValidation,
        form,
        formPosition,
        showFormSubContent,
        hideProductOverview,
        hideFooterCTA,
        showKeyValueProps,
        keyValuePropsHeader,
        keyValueProps,
        productSocialProof,
      },
    },
  },
  pageContext: { nodeType },
}) => {
  const theme = useTheme()

  return (
    <BaseLayout noFooter>
      <HelmetSEO
        title={seoTitle}
        description={description}
        image={socialMediaImage}
        noIndex
      />
      <NodeTypeTracker nodeType={nodeType} />
      <Hero css={{ background: theme.brand.darkViolet }} contrast={false}>
        <HeroContent>
          <Container>
            <div
              css={{
                display: 'grid',
                gap: theme.space.base,
                alignItems: 'center',

                [theme.mq.medium]: {
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: theme.space.xlarge,
                },
              }}
            >
              {formPosition !== 'left' && (
                <TitleSection
                  heading={hero.heading}
                  content={hero.content}
                  headingClass="h2"
                  textAlign="center"
                  contrastClass="u-contrast"
                  formPosition={formPosition}
                />
              )}
              <FormSection
                form={form}
                enableWorkEmailValidation={enableWorkEmailValidation}
                freeSignupUseGenericEmail={freeSignupUseGenericEmail}
                showFormSubContent={showFormSubContent}
              />
              {formPosition === 'left' && (
                <TitleSection
                  heading={hero.heading}
                  content={hero.content}
                  headingClass="h4"
                  textAlign="center"
                  contrastClass="u-contrast"
                  formPosition={formPosition}
                />
              )}
            </div>
          </Container>
        </HeroContent>
      </Hero>
      {showKeyValueProps && (
        <Section
          css={{
            paddingTop: `${theme.space['2xlarge']}px !important`,
          }}
        >
          <Container>
            {keyValuePropsHeader && (
              <SectionTitle
                as="h3"
                align={{ medium: 'center' }}
                title={<Data>{keyValuePropsHeader}</Data>}
              />
            )}
            <Row gutter={{ base: 'large' }}>
              {keyValueProps.map((prop) => (
                <KeyValueProp key={prop.heading} data={prop} />
              ))}
            </Row>
          </Container>
        </Section>
      )}
      {!hideProductOverview && <ProductOverview />}
      {hideProductOverview && (
        <Section
          css={{
            background: theme.brand.darkViolet,
            position: 'relative',
          }}
          space="small"
        >
          <Container className="u-contrast">
            <SectionTitle
              as="h3"
              title={<Data>{productSocialProof.heading}</Data>}
              sub={<Data>{productSocialProof.blurb}</Data>}
            />
          </Container>
          <CustomerLogos contrast />
        </Section>
      )}
      {!hideFooterCTA && <FooterCTA />}
      <StaticFooter />
    </BaseLayout>
  )
}

SignUpLandingPage.propTypes = {
  data: PropTypes.object.isRequired,
  pageContext: PropTypes.object.isRequired,
}

export default SignUpLandingPage

export const pageQuery = graphql`
  query SignUpLandingPageSingle($slug: String!) {
    mdx(
      fields: { slug: { eq: $slug } }
      fileAbsolutePath: { regex: "/src/sign-up-landing-pages/" }
    ) {
      fileAbsolutePath
      frontmatter {
        seoTitle
        freeSignupUseGenericEmail
        enableWorkEmailValidation
        description
        slug
        socialMediaImage
        hero {
          heading
          content
        }
        form {
          embed
          programId
          thankYouMessage
          heading
          content
        }
        formPosition
        showFormSubContent
        hideProductOverview
        hideFooterCTA
        showKeyValueProps
        keyValuePropsHeader
        keyValueProps {
          heading
          feature
          blurb
        }
        productSocialProof {
          heading
          blurb
        }
      }
      fields {
        slug
      }
    }
  }
`
